import { getAllClinicsQuery } from '@/api/clinics/clinicQueries';
import { useCreateQrCodeMutation } from '@/api/qr-codes/qrCodeMutations';
import { getAllUsersQuery } from '@/api/users/usersQueries';
import { Button } from '@/components/common/Button';
import { Combobox } from '@/components/common/Combobox';
import { DialogWrapper } from '@/components/common/DialogWrapper';
import { Select } from '@/components/common/Select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

type QrType = 'doctor' | 'clinic';

const QR_TYPE_OPTIONS = [
  { label: 'Doctor', value: 'doctor' },
  { label: 'Clinic', value: 'clinic' },
];

const DOCTOR_TITLE_OPTIONS = [
  { label: 'Med. praktikė', value: 'Med. praktikė' },
  { label: 'Gyd.', value: 'Gyd.' },
  { label: 'Slaugytoja', value: 'Slaugytoja' },
  { label: 'Dietistė', value: 'Dietistė' },
];

interface CreateQrCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateQrCodeDialog({
  open,
  onOpenChange,
}: CreateQrCodeDialogProps) {
  const [qrType, setQrType] = useState<QrType | ''>('');
  const [doctorTitle, setDoctorTitle] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(
    undefined,
  );
  const [selectedClinicId, setSelectedClinicId] = useState('');

  const { data: doctors } = useQuery({
    ...getAllUsersQuery({ role: 'Doctor', per_page: 100 }),
    enabled: open && qrType === 'doctor',
  });

  const { data: clinics } = useQuery({
    ...getAllClinicsQuery(),
    enabled: open && (qrType === 'clinic' || qrType === 'doctor'),
  });

  const createQrCodeMutation = useMutation(useCreateQrCodeMutation());

  const doctorComboboxItems =
    doctors?.map((doctor) => ({
      id: doctor.id,
      value: `${doctor.name} ${doctor.surname}`,
      label: `${doctor.name} ${doctor.surname}`,
    })) ?? [];

  const clinicOptions =
    clinics?.map((clinic) => ({
      label: clinic.name,
      value: clinic.id.toString(),
    })) ?? [];

  const isGenerateDisabled = () => {
    if (!qrType) return true;
    if (
      qrType === 'doctor' &&
      (!doctorTitle || !selectedDoctorId || !selectedClinicId)
    )
      return true;
    if (qrType === 'clinic' && !selectedClinicId) return true;
    return createQrCodeMutation.isPending;
  };

  const handleGenerate = () => {
    if (!qrType) return;

    const url = 'https://eternalife.typeform.com/to/bSzZNRV2';

    if (qrType === 'doctor') {
      const doctor = doctors?.find((d) => d.id.toString() === selectedDoctorId);
      if (!doctor) return;

      const clinic = clinics?.find((c) => c.id.toString() === selectedClinicId);

      const baseData = {
        doctor_name: doctor.name,
        doctor_surname: doctor.surname,
        doctor_title: doctorTitle,
        url,
        clinic_name: clinic?.name ?? '',
        doctor_id: doctor.id,
        is_clinic: 'no' as const,
      };

      if (doctorTitle !== 'Slaugytoja') {
        createQrCodeMutation.mutate(
          {
            ...baseData,
            first_row: 'Personalizuoti papildai per 2 žingsnius:',
            second_row: '1. Įveskite vardą, pavardę, telefoną ir el. paštą',
            third_row:
              '2. Pasirinkite kontaktavimo būdą: skambutis, el. paštas ar 15 min. video skambutis.',
          },
          {
            onSuccess: () => {
              resetForm();
              onOpenChange(false);
            },
          },
        );
      } else {
        createQrCodeMutation.mutate(baseData, {
          onSuccess: () => {
            resetForm();
            onOpenChange(false);
          },
        });
      }
    } else {
      const clinic = clinics?.find((c) => c.id.toString() === selectedClinicId);
      if (!clinic) return;

      createQrCodeMutation.mutate(
        {
          clinic_id: clinic.id,
          url,
          doctor_name: clinic.name,
          clinic_name: clinic.name,
          is_clinic: 'yes' as const,
        },
        {
          onSuccess: () => {
            resetForm();
            onOpenChange(false);
          },
        },
      );
    }
  };

  const resetForm = () => {
    setQrType('');
    setDoctorTitle('');
    setSelectedDoctorId(undefined);
    setSelectedClinicId('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  return (
    <DialogWrapper
      open={open}
      onOpenChange={handleOpenChange}
      title="Add new QR code"
    >
      <div className="flex flex-col gap-6">
        <Select
          label="Select QR type"
          options={QR_TYPE_OPTIONS}
          value={qrType}
          onChange={(value) => {
            setQrType(value as QrType);
            setDoctorTitle('');
            setSelectedDoctorId(undefined);
            setSelectedClinicId('');
          }}
        />

        {qrType === 'doctor' && (
          <>
            <Select
              label="Select doctor title"
              options={DOCTOR_TITLE_OPTIONS}
              value={doctorTitle}
              onChange={setDoctorTitle}
            />

            <Combobox
              label="Select doctor"
              items={doctorComboboxItems}
              value={selectedDoctorId}
              onChange={setSelectedDoctorId}
              emptyText="No doctors found."
              className="bg-white"
            />

            <Select
              label="Select clinic"
              options={clinicOptions}
              value={selectedClinicId}
              onChange={setSelectedClinicId}
            />
          </>
        )}

        {qrType === 'clinic' && (
          <Select
            label="Select clinic"
            options={clinicOptions}
            value={selectedClinicId}
            onChange={setSelectedClinicId}
          />
        )}

        <Button
          variant="primary"
          size="md"
          onClick={handleGenerate}
          disabled={isGenerateDisabled()}
          className="self-start"
        >
          {createQrCodeMutation.isPending
            ? 'Generating...'
            : 'Generate QR code'}
        </Button>
      </div>
    </DialogWrapper>
  );
}
